import { Observable, of, shareReplay, tap } from 'rxjs';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { AdditionalParams } from '@irian-ro/abstract-resource';

type CacheEntry = {
  response$: Observable<any>;
  timestamp: number;
  resourceType: string;
  cacheable: boolean;
  findAll: (query: any, additionalParams: Array<AdditionalParams>) => Observable<any>;
};

type CacheState = {
  values: Map<string, CacheEntry>;
};

const initialState: CacheState = {
  values: new Map(),
};

export const CacheResourceStore = signalStore(
  { providedIn: 'root', protectedState: false },
  withState(initialState),

  withMethods((store) => ({
    patchQuery: (
      query: string,
      response$: Observable<any>,
      resourceType: string,
      cacheable: boolean,
      findAll: (query: any, additionalParams: Array<AdditionalParams>) => Observable<any>
    ) => {
      const currentValues = store.values();
      const updatedValues = new Map(currentValues);

      updatedValues.set(query, {
        response$,
        timestamp: Date.now(),
        resourceType,
        cacheable,
        findAll,
      });

      patchState(store, { values: updatedValues });
    },
  })),

  withMethods((store) => ({
    getCachedResources: (
      query: string,
      additionalParams: any[],
      resourceType: string,
      cacheable: boolean,
      cacheDuration: number,
      findAll: (query: any, additionalParams: Array<AdditionalParams>) => Observable<any>
    ): Observable<any> => {
      const cacheKey = JSON.stringify({ resourceType, query, additionalParams });
      const cachedEntry = store.values().get(cacheKey);
      const currentTime = Date.now();

      if (!cacheable) {
        return findAll(JSON.parse(query), additionalParams);
      }

      if (cachedEntry) {
        const isCacheValid = (currentTime - cachedEntry.timestamp) < cacheDuration;

        if (isCacheValid) {
          return cachedEntry.response$;
        }
      }

      const response$ = findAll(JSON.parse(query), additionalParams).pipe(
        tap((response) => {
          store.patchQuery(cacheKey, of(response), resourceType, cacheable, findAll);
        }),
        shareReplay(1)
      );

      store.patchQuery(cacheKey, response$, resourceType, cacheable, findAll);

      return response$;
    },
  }))
);
